import React, { useEffect, useState } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Card,
  Stack,
  Alert,
  Box,
  Divider,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik, FormikProvider, Form } from "formik";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import {
  getPayoutScheduleUrl,
  getEffectivePayoutScheduleUrl,
} from "src/react-query/endPoints";
import { payoutScheduleValidationSchema } from "src/util/validationSchemas";
import { payoutScheduleDefaultValues } from "../company/company.constants";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import { Loader } from "../Loader";

const FREQUENCY_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY_NTH_WEEKDAY", label: "Nth Weekday of Month" },
  { value: "MONTHLY_FIXED_DATE", label: "Fixed Date of Month" },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const NTH_OCCURRENCE_OPTIONS = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

const getDayName = (val) =>
  DAY_OF_WEEK_OPTIONS.find((d) => d.value === val)?.label || "";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return "N/A";
  }
};

const getOrdinalSuffix = (n) => {
  if (!n) return "";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

const getScheduleDescription = (schedule) => {
  if (!schedule) return "No schedule configured";
  const { frequencyType, dayOfWeek, daysOfWeek, nthOccurrence, dayOfMonth, daysOfMonth } = schedule;

  switch (frequencyType) {
    case "WEEKLY": {
      const days = daysOfWeek && daysOfWeek.length > 0
        ? daysOfWeek.map(getDayName).join(", ")
        : getDayName(dayOfWeek);
      return `Every ${days}`;
    }
    case "MONTHLY_NTH_WEEKDAY": {
      const dayName = getDayName(dayOfWeek);
      const nthName = NTH_OCCURRENCE_OPTIONS.find((n) => n.value === nthOccurrence)?.label || "";
      return `${nthName} ${dayName} of every month`;
    }
    case "MONTHLY_FIXED_DATE": {
      const dates = daysOfMonth && daysOfMonth.length > 0
        ? daysOfMonth.map((d) => `${d}${getOrdinalSuffix(d)}`).join(", ")
        : `${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}`;
      return `${dates} of every month`;
    }
    default:
      return "Unknown schedule";
  }
};

/**
 * Reads schedule data and returns normalized daysOfWeek array.
 * Falls back to wrapping legacy dayOfWeek in an array.
 */
const normalizeDaysOfWeek = (schedule) => {
  if (!schedule) return [5]; // default Friday
  if (schedule.daysOfWeek && schedule.daysOfWeek.length > 0) return schedule.daysOfWeek;
  if (schedule.dayOfWeek != null) return [schedule.dayOfWeek];
  return [];
};

/**
 * Reads schedule data and returns normalized daysOfMonth array.
 * Falls back to wrapping legacy dayOfMonth in an array.
 */
const normalizeDaysOfMonth = (schedule) => {
  if (!schedule) return [];
  if (schedule.daysOfMonth && schedule.daysOfMonth.length > 0) return schedule.daysOfMonth;
  if (schedule.dayOfMonth != null) return [schedule.dayOfMonth];
  return [];
};

const TeamPayoutScheduleTab = ({ teamId, parentCompanyId, isReadOnly }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {
    actions: { setAlert },
  } = alertSlice;

  const [inheritFromParent, setInheritFromParent] = useState(true);

  // Fetch effective schedule (resolves parent inheritance)
  const {
    data: effectiveData,
    isFetching,
    isError,
  } = useQuery(
    queryKeys.effectivePayoutSchedule(null, teamId, parentCompanyId),
    fetchWithError,
    {
      enabled: !!teamId,
      retry: false,
      onError: () => {},
    }
  );

  // Mutation for updating team's payout schedule
  const payoutScheduleMutation = useMutation(fetchApi, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["effectivePayoutSchedule"]);
      queryClient.invalidateQueries(["payoutSchedule"]);
      dispatch(
        setAlert({
          open: true,
          message: data?.message || "Payout schedule updated successfully",
          severity: "success",
        })
      );
    },
    onError: (error) => {
      dispatch(
        setAlert({
          open: true,
          message:
            error?.response?.data?.message ||
            "Failed to update payout schedule",
          severity: "error",
        })
      );
    },
  });

  // Form submit handler
  const onSubmit = async (scheduleData, { setSubmitting }) => {
    const {
      frequencyType,
      daysOfWeek,
      dayOfWeek,
      nthOccurrence,
      daysOfMonth,
      isActive,
    } = scheduleData;

    const body = {
      frequencyType,
      isActive,
      inheritFromParent,
    };

    if (frequencyType === "WEEKLY") {
      body.daysOfWeek = daysOfWeek || [];
    } else if (frequencyType === "MONTHLY_NTH_WEEKDAY") {
      body.dayOfWeek = dayOfWeek;
      body.nthOccurrence = nthOccurrence;
    } else if (frequencyType === "MONTHLY_FIXED_DATE") {
      body.daysOfMonth = daysOfMonth || [];
    }

    payoutScheduleMutation.mutate({
      url: getPayoutScheduleUrl(teamId),
      method: "PUT",
      body,
    });
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: payoutScheduleDefaultValues(),
    validationSchema: payoutScheduleValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    resetForm,
  } = formik;

  // Helper to build form values from a schedule object
  const buildFormValues = (schedule) => ({
    frequencyType: schedule.frequencyType || "WEEKLY",
    dayOfWeek: schedule.dayOfWeek,
    daysOfWeek: normalizeDaysOfWeek(schedule),
    nthOccurrence: schedule.nthOccurrence,
    dayOfMonth: schedule.dayOfMonth,
    daysOfMonth: normalizeDaysOfMonth(schedule),
    isActive: schedule.isActive ?? true,
    lastRunAt: schedule.lastRunAt,
    lastRunStatus: schedule.lastRunStatus,
  });

  // Populate form when data loads
  useEffect(() => {
    if (effectiveData) {
      const { teamSchedule, inherited, parentSchedule } = effectiveData;

      // Determine inherit state
      if (teamSchedule) {
        setInheritFromParent(teamSchedule.inheritFromParent ?? true);
      } else {
        setInheritFromParent(true);
      }

      // Populate form with team's own values (for override mode)
      const scheduleToUse = teamSchedule || parentSchedule;
      if (scheduleToUse) {
        resetForm({ values: buildFormValues(scheduleToUse) });
      }
    }
  }, [effectiveData, resetForm]);

  const handleInheritToggle = (e) => {
    const newValue = e.target.checked;
    setInheritFromParent(newValue);

    if (newValue && effectiveData?.parentSchedule) {
      resetForm({ values: buildFormValues(effectiveData.parentSchedule) });
    } else if (!newValue && effectiveData?.teamSchedule) {
      resetForm({ values: buildFormValues(effectiveData.teamSchedule) });
    }
  };

  const showDaysOfWeek = values.frequencyType === "WEEKLY";
  const showSingleDayOfWeek = values.frequencyType === "MONTHLY_NTH_WEEKDAY";
  const showNthOccurrence = values.frequencyType === "MONTHLY_NTH_WEEKDAY";
  const showDaysOfMonth = values.frequencyType === "MONTHLY_FIXED_DATE";

  const handleFrequencyChange = (e) => {
    const newType = e.target.value;
    setFieldValue("frequencyType", newType);
    if (newType === "WEEKLY") {
      setFieldValue("nthOccurrence", null);
      setFieldValue("dayOfMonth", null);
      setFieldValue("daysOfMonth", []);
      if (!values.daysOfWeek || values.daysOfWeek.length === 0) {
        setFieldValue("daysOfWeek", [5]); // default to Friday
      }
    } else if (newType === "MONTHLY_NTH_WEEKDAY") {
      setFieldValue("daysOfWeek", []);
      setFieldValue("dayOfMonth", null);
      setFieldValue("daysOfMonth", []);
    } else if (newType === "MONTHLY_FIXED_DATE") {
      setFieldValue("dayOfWeek", null);
      setFieldValue("daysOfWeek", []);
      setFieldValue("nthOccurrence", null);
      if (!values.daysOfMonth || values.daysOfMonth.length === 0) {
        setFieldValue("daysOfMonth", [1]); // default to 1st
      }
    }
  };

  const isFormDisabled = isReadOnly || inheritFromParent;
  const parentSchedule = effectiveData?.parentSchedule;

  if (isFetching) {
    return <Loader />;
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payout Schedule Configuration
          </Typography>

          {/* Inherit from parent toggle */}
          {parentCompanyId && (
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={inheritFromParent}
                    onChange={handleInheritToggle}
                    disabled={isReadOnly}
                  />
                }
                label="Inherit schedule from parent company"
              />
              {inheritFromParent && parentSchedule && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This team uses the parent company's payout schedule:{" "}
                  <strong>{getScheduleDescription(parentSchedule)}</strong>
                </Alert>
              )}
              {inheritFromParent && !parentSchedule && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No payout schedule is configured for the parent company.
                  Please configure one at the company level or switch to a custom schedule.
                </Alert>
              )}
            </Box>
          )}

          {!inheritFromParent && parentCompanyId && (
            <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
              Custom schedule override enabled. This team will use its own
              schedule instead of the parent company's.
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Schedule fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="frequency-type-label">Frequency Type</InputLabel>
                <Select
                  required
                  labelId="frequency-type-label"
                  id="frequency-type"
                  label="Frequency Type"
                  value={values.frequencyType || ""}
                  onChange={handleFrequencyChange}
                  error={Boolean(touched.frequencyType && errors.frequencyType)}
                  disabled={isFormDisabled}
                >
                  {FREQUENCY_OPTIONS.map(({ value, label }) => (
                    <MenuItem value={value} key={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* WEEKLY: Multi-select days of week with chips */}
            {showDaysOfWeek && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="days-of-week-label">Days of Week</InputLabel>
                  <Select
                    multiple
                    labelId="days-of-week-label"
                    id="days-of-week"
                    label="Days of Week"
                    value={values.daysOfWeek || []}
                    onChange={(e) => setFieldValue("daysOfWeek", e.target.value)}
                    error={Boolean(touched.daysOfWeek && errors.daysOfWeek)}
                    disabled={isFormDisabled}
                    input={<OutlinedInput label="Days of Week" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected
                          .sort((a, b) => a - b)
                          .map((val) => (
                            <Chip key={val} label={getDayName(val)} size="small" />
                          ))}
                      </Box>
                    )}
                  >
                    {DAY_OF_WEEK_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        <Checkbox checked={(values.daysOfWeek || []).indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* MONTHLY_NTH_WEEKDAY: Single day select (unchanged) */}
            {showSingleDayOfWeek && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="day-of-week-label">Day of Week</InputLabel>
                  <Select
                    required
                    labelId="day-of-week-label"
                    id="day-of-week"
                    label="Day of Week"
                    value={values.dayOfWeek ?? ""}
                    {...getFieldProps("dayOfWeek")}
                    error={Boolean(touched.dayOfWeek && errors.dayOfWeek)}
                    disabled={isFormDisabled}
                  >
                    {DAY_OF_WEEK_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {showNthOccurrence && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="nth-occurrence-label">Occurrence</InputLabel>
                  <Select
                    required
                    labelId="nth-occurrence-label"
                    id="nth-occurrence"
                    label="Occurrence"
                    value={values.nthOccurrence ?? ""}
                    {...getFieldProps("nthOccurrence")}
                    error={Boolean(
                      touched.nthOccurrence && errors.nthOccurrence
                    )}
                    disabled={isFormDisabled}
                  >
                    {NTH_OCCURRENCE_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* MONTHLY_FIXED_DATE: Multi-select days of month with chips */}
            {showDaysOfMonth && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="days-of-month-label">Days of Month</InputLabel>
                  <Select
                    multiple
                    labelId="days-of-month-label"
                    id="days-of-month"
                    label="Days of Month"
                    value={values.daysOfMonth || []}
                    onChange={(e) => setFieldValue("daysOfMonth", e.target.value)}
                    error={Boolean(touched.daysOfMonth && errors.daysOfMonth)}
                    disabled={isFormDisabled}
                    input={<OutlinedInput label="Days of Month" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected
                          .sort((a, b) => a - b)
                          .map((val) => (
                            <Chip
                              key={val}
                              label={`${val}${getOrdinalSuffix(val)}`}
                              size="small"
                            />
                          ))}
                      </Box>
                    )}
                  >
                    {DAY_OF_MONTH_OPTIONS.map(({ value, label }) => (
                      <MenuItem value={value} key={value}>
                        <Checkbox checked={(values.daysOfMonth || []).indexOf(value) > -1} />
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(values.isActive)}
                    onChange={(e) =>
                      setFieldValue("isActive", e.target.checked)
                    }
                    disabled={isFormDisabled}
                  />
                }
                label="Schedule Active"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Last Run At"
                value={formatDate(values.lastRunAt)}
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Last Run Status
              </Typography>
              {values.lastRunStatus === "SUCCESS" ? (
                <Chip label="SUCCESS" color="success" size="small" />
              ) : values.lastRunStatus === "FAILED" ? (
                <Chip label="FAILED" color="error" size="small" />
              ) : (
                <Chip label="N/A" size="small" />
              )}
            </Grid>
          </Grid>

          {/* Save button */}
          {!isReadOnly && (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 3 }}
            >
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting || payoutScheduleMutation.isLoading}
              >
                Save
              </LoadingButton>
            </Stack>
          )}

          {isError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No payout schedule found for this team. Please contact your
              administrator to set up a schedule.
            </Alert>
          )}
        </Card>
      </Form>
    </FormikProvider>
  );
};

export default TeamPayoutScheduleTab;
